import type { SupplierStatus } from '@/enum/supplier'
import { config } from '@/config/env'

// Interfaces para los datos del proveedor
export interface GeneralProfileData {
  legalName: string
  commercialName?: string
  vatType: string
  serviceTypeId: string
  country: string
  state: string
  city: string
  municipality: string
  suburb: string
  postalCode: string
  street: string
  externalNumber: string
  internalNumber?: string
  mainContactName: string
  mainContactPhone: string
  salesEmail: string
  accountsPayableEmail: string
  internalRequesterName: string
  /** general, servicios o fletes (SP-214). */
  supplierType?: string
}

export interface FiscalProfileData {
  taxRegime: string
  cfdiUse: string
  taxEmail: string
  taxPhone: string
}

export interface BankAccountData {
  accountHolderName?: string
  bankName: string
  clabe: string
  currency: string
  accountNumber?: string
  file?: File | null
}

export interface DocumentType {
  id: string
  code: string
  name: string
  description?: string
  isRequired: boolean
  personType?: string
  validityMonths?: number
  maxFileSizeMb: number
  allowedMimeTypes: string[]
}

// Estado de vigencia del documento. Lo calcula el job diario J-02 (SP-226) y lo
// expone el backend como `estado_vigencia` (aquí en camelCase `estadoVigencia`).
// El frontend NO lo calcula: solo consume el valor para pintar el badge.
export type EstadoVigencia = 'vigente' | 'por_vencer' | 'vencido' | 'sin_fecha'

export interface SupplierDocument {
  id: string
  version: number
  fileName: string
  fileUrl: string
  status: 'pending_review' | 'approved' | 'rejected'
  uploadedAt: string
  reviewedAt?: string
  reviewComment?: string
  validUntil?: string | null
  estadoVigencia?: EstadoVigencia
}

export interface DocumentRequirement {
  documentType: DocumentType
  latestDocument: SupplierDocument | null
}

export interface ProfileCompletion {
  completionPercentage: number
  isComplete: boolean
  missingFields: string[]
  missingDocuments: string[]
  sections: {
    general: { completed: boolean; percentage: number; missingFields: string[] }
    fiscal: { completed: boolean; percentage: number; missingFields: string[] }
    bankAccount: { completed: boolean; percentage: number; missingFields: string[] }
    documents: { completed: boolean; percentage: number; missingDocuments: string[] }
  }
}

export interface ProfileDashboardData {
  status: SupplierStatus;
  completionPercentage: number;
  lastUpdate: string;
  message?: string
}

export interface SupplierProfileResponse {
  general: {
    id: string
    rfc: string
    /** general, servicios o fletes (SP-214). Null en los proveedores de antes. */
    supplierType?: string | null
    legalName: string
    commercialName: string | null
    personType: string
    vatType: string
    serviceTypeId: string
    country: string
    state: string
    city: string
    municipality: string
    suburb: string
    postalCode: string
    street: string
    externalNumber: string
    internalNumber: string
    mainContactName: string
    mainContactPhone: string
    salesEmail: string
    accountsPayableEmail: string
    internalRequesterName: string
    repseNumber: string | null
    taxRegime: string
    cfdiUse: string
    taxEmail: string
    taxPhone: string
    origin: string
    status: string
    // Días de crédito acordados (1-60). Lo define SLYRSA desde el panel; el
    // portal del proveedor solo lo muestra.
    creditDays?: number | null
    createdAt: string
    updatedAt: string
  }
  fiscal: {
    taxRegime: string
    cfdiUse: string
    taxEmail: string
    taxPhone: string
  } | null
  primaryBankAccount: {
    id: string
    supplierId: string
    accountHolderName: string | null
    bankName: string
    clabe: string
    accountNumber?: string | null
    currency: string
    isPrimary: boolean
    status: string
    createdAt: string
    updatedAt: string
    coverUrl: string | null
    coverOriginalName: string | null
  } | null
  documents: any[]
}

// Helper para obtener el token del proveedor
const getSupplierToken = (): string | null => {
  return localStorage.getItem('supplier_access_token')
}

// Servicio para actualizar perfil general
export const updateGeneralProfile = async (data: Partial<GeneralProfileData>) => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile/general`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}

// Servicio para actualizar información fiscal
export const updateFiscalProfile = async (data: FiscalProfileData) => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile/fiscal`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}

// Servicio para crear/actualizar cuenta bancaria
export const updateBankAccount = async (data: BankAccountData) => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const formData = new FormData()
  formData.append('bankName', data.bankName)
  formData.append('clabe', data.clabe)
  formData.append('currency', data.currency)

  if (data.accountHolderName) {
    formData.append('accountHolderName', data.accountHolderName)
  }

  if (data.accountNumber) {
    formData.append('accountNumber', data.accountNumber)
  }

  if (data.file) {
    formData.append('file', data.file)
  }

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile/bank-account`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}

// Servicio para obtener el perfil completo del proveedor
export const getSupplierProfile = async (): Promise<SupplierProfileResponse> => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}

// Servicio para obtener documentos requeridos
export const getSupplierDocuments = async (): Promise<DocumentRequirement[]> => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile/documents`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}

/**
 * Sube un documento del expediente o de los financieros.
 *
 * `validUntil` la captura el proveedor a mano al subir (confirmado con Karen):
 * no se deriva de nada. Va en formato YYYY-MM-DD y es opcional — si el tipo de
 * documento no tiene vigencia, no se manda y queda en null.
 */
export const uploadSupplierDocument = async (
  documentTypeId: string,
  file: File,
  validUntil?: string
): Promise<SupplierDocument> => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const formData = new FormData()
  formData.append('documentTypeId', documentTypeId)
  formData.append('file', file)
  if (validUntil) formData.append('validUntil', validUntil)

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile/documents`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
      // NO incluir Content-Type, el navegador lo establece automáticamente con el boundary
    },
    body: formData
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}

// Servicio para verificar completitud del perfil
export const getProfileCompletion = async (): Promise<ProfileCompletion> => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile/completion`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}

// Servicio para obtener data del dashboard
export const getProfileDashboard = async (): Promise<ProfileDashboardData> => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile/dashboard`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}

// Servicio para enviar perfil a revisión
export const submitProfileForReview = async (): Promise<{
  success?: boolean
  message: string
  status?: string
  submittedAt?: string
  folio?: string
  supplierId?: string
}> => {
  const token = getSupplierToken()

  if (!token) {
    throw new Error('No se encontró token de autenticación')
  }

  const response = await fetch(`${config.apiBaseUrl}/suppliers/profile/submit-for-review`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({})
  })

  if (!response.ok) {
    const error = await response.json()
    throw error
  }

  return response.json()
}
